import { useCallback, useEffect, useMemo, useState } from 'react';
import { StatusBadge } from '@/components/StatusBadge';
import { DatePicker } from '@/components/ui/date-picker';
import { useProjectPeople, useProjectPicklists, PROJECT_STATUS, type ProjectRow, type ProjectTask } from '@/api/projects';
import type { BadgeKind } from '@/lib/cobalt-helpers';
import { cn } from '@/lib/utils';

/**
 * Building blocks for the PSA Projects pages (dev/planning/Projects.md). Status chips always carry a
 * label; colours come from the theme status tokens (never colour alone).
 */

// Autotask web links use its documented ExecuteCommand entry point (works across UI versions).
const AT_EXEC = 'https://ww31.autotask.net/Autotask/AutotaskExtend/ExecuteCommand.aspx';
export const autotaskProjectUrl = (id: number) => `${AT_EXEC}?Code=OpenProject&ProjectID=${id}`;
export const autotaskTaskUrl = (id: number) => `${AT_EXEC}?Code=OpenTaskDetail&TaskID=${id}`;

export const fmtHours = (h: number | null | undefined) => {
  const n = Number(h) || 0;
  if (n === 0) return '0h';
  if (n < 1) return `${Math.round(n * 60)}m`;
  return `${Math.round(n * 10) / 10}h`;
};

/** "12 Oct" style calendar date from YYYY-MM-DD. */
export function fmtDay(day: string | null | undefined) {
  if (!day) return '—';
  const d = new Date(`${day}T00:00:00`);
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', ...(d.getFullYear() !== new Date().getFullYear() && { year: '2-digit' }) });
}

/** Today in Melbourne as YYYY-MM-DD (task dates are calendar dates). */
export function melbourneToday() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Melbourne', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}

export function projectStatusKind(status: number): BadgeKind {
  if (status === PROJECT_STATUS.complete) return 'success';
  if (status === PROJECT_STATUS.pendingCompletion) return 'warning';
  if (status === PROJECT_STATUS.inactive) return 'neutral';
  if (status === PROJECT_STATUS.new) return 'info';
  return 'brand';
}

export function taskStatusKind(status: number): BadgeKind {
  if (status === 5 || status === 21) return 'success';
  if ([7, 19, 20, 22, 26].includes(status)) return 'warning';
  if (status === 23) return 'destructive';
  if (status === 8) return 'brand';
  if (status === 1) return 'info';
  return 'neutral';
}

/** Picklist label lookups (project + task statuses), with the raw value as a fallback. */
export function useProjectLabels() {
  const { data } = useProjectPicklists();
  return useMemo(() => {
    const map = (xs?: { value: string; label: string }[]) => new Map((xs ?? []).map((x) => [Number(x.value), x.label]));
    const ps = map(data?.projectStatus); const ts = map(data?.taskStatusAll); const wt = new Map((data?.workTypes ?? []).map((w) => [w.id, w.name]));
    return {
      projectStatus: (v: number | null | undefined) => (v == null ? '—' : ps.get(v)?.replace(/\s+/g, ' ') ?? String(v)),
      taskStatus: (v: number | null | undefined) => (v == null ? '—' : ts.get(v) ?? String(v)),
      workType: (v: number | null | undefined) => (v == null ? 'No work type' : wt.get(v) ?? `#${v}`),
      role: (v: number | null | undefined) => (v == null ? '—' : data?.roleNames[String(v)] ?? `#${v}`),
    };
  }, [data]);
}

export function ProjectStatusChip({ status }: { status: number }) {
  const l = useProjectLabels();
  return <StatusBadge kind={projectStatusKind(status)} dot>{l.projectStatus(status)}</StatusBadge>;
}

export function TaskStatusChip({ status }: { status: number }) {
  const l = useProjectLabels();
  return <StatusBadge kind={taskStatusKind(status)}>{l.taskStatus(status)}</StatusBadge>;
}

/** Cosmos progress (estimated hours of completed tasks). */
export function ProgressBar({ pct, className, showLabel = true }: { pct: number; className?: string; showLabel?: boolean }) {
  const v = Math.max(0, Math.min(100, pct));
  return (
    <div className={cn('flex items-center gap-2', className)} title={`${pct}% of estimated hours complete`}>
      <div className="h-1.5 min-w-[60px] flex-1 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-[var(--chart-series-1)]" style={{ width: `${v}%` }} />
      </div>
      {showLabel && <span className="w-9 shrink-0 text-right text-xs tabular-nums text-muted-foreground">{Math.round(pct)}%</span>}
    </div>
  );
}

/** Logged vs estimated hours: one bar, amber past 85 %, red past 100 % (Proxuma's est/actual bars). */
export function HoursBar({ logged, estimate, className }: { logged: number; estimate: number; className?: string }) {
  const ratio = estimate > 0 ? logged / estimate : logged > 0 ? 2 : 0;
  const tone = ratio > 1 ? 'bg-destructive' : ratio >= 0.85 ? 'bg-warning' : 'bg-muted-foreground/50';
  return (
    <div className={cn('min-w-[90px]', className)} title={`${fmtHours(logged)} logged of ${fmtHours(estimate)} estimated`}>
      <div className="flex items-baseline justify-between gap-2 text-xs tabular-nums">
        <span className={cn('font-medium', ratio > 1 ? 'text-destructive' : 'text-foreground')}>{fmtHours(logged)}</span>
        <span className="text-muted-foreground">/ {fmtHours(estimate)}</span>
      </div>
      <div className="mt-1 h-1 overflow-hidden rounded-full bg-muted">
        <div className={cn('h-full rounded-full', tone)} style={{ width: `${Math.min(100, ratio * 100)}%` }} />
      </div>
    </div>
  );
}

/** Small labelled flag (never colour alone). */
export function Flag({ tone, children, title }: { tone: 'bad' | 'warn' | 'muted'; children: React.ReactNode; title?: string }) {
  const cls = tone === 'bad' ? 'bg-destructive-soft text-destructive' : tone === 'warn' ? 'bg-warning-soft text-warning' : 'bg-muted text-muted-foreground';
  return <span title={title} className={cn('inline-flex items-center whitespace-nowrap rounded px-1.5 py-px text-[10.5px] font-semibold', cls)}>{children}</span>;
}

export function TaskFlags({ t }: { t: ProjectTask }) {
  return (
    <span className="inline-flex flex-wrap gap-1">
      {t.flags.unassigned && <Flag tone="warn">Unassigned</Flag>}
      {t.flags.noEstimate && <Flag tone="muted">No estimate</Flag>}
      {!t.done && t.flags.noWorkType && <Flag tone="muted" title="Autotask needs a work type once a task is assigned. Cosmos fills Professional Services/AD-HOC.">No work type</Flag>}
      {t.flags.doneWithoutTime && <Flag tone="warn" title="Completed with no time logged">No time</Flag>}
    </span>
  );
}

/** Past-end is shown by the red end date, not a pill (decluttered 2026-10-16). */
export function ProjectFlags({ p }: { p: ProjectRow }) {
  return (
    <span className="inline-flex flex-wrap gap-1">
      {p.readyForPendingCompletion && <Flag tone="warn" title="Every task is done. Set Pending Completion.">Ready to close</Flag>}
      {p.stale && <Flag tone="muted">No recent activity</Flag>}
    </span>
  );
}

// ---------------- Pickers ----------------

const selectCls = 'h-7 max-w-full rounded-md border border-input bg-background px-1.5 text-xs text-foreground disabled:opacity-50';

/** Status tint for a closed select (same tokens as StatusBadge); the open list stays neutral. */
const KIND_SELECT: Record<BadgeKind, string> = {
  brand: 'bg-brand-soft text-brand-strong dark:text-brand',
  info: 'bg-info-soft text-info',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  destructive: 'bg-destructive-soft text-destructive',
  neutral: 'bg-neutral-soft text-neutral dark:text-muted-foreground',
};

/** Task status select, colour-coded by status. Never offers Complete-Pending Customer (decided 2026-10-16). */
export function TaskStatusSelect({ value, onChange, disabled, className }: { value: number; onChange: (v: number) => void; disabled?: boolean; className?: string }) {
  const { data } = useProjectPicklists();
  const l = useProjectLabels();
  const options = data?.taskStatus ?? [];
  const known = options.some((o) => Number(o.value) === value);
  return (
    <select value={value} disabled={disabled} onChange={(e) => onChange(Number(e.target.value))} onClick={(e) => e.stopPropagation()}
      className={cn(selectCls, 'border-transparent font-medium [&>option]:bg-background [&>option]:font-normal [&>option]:text-foreground', KIND_SELECT[taskStatusKind(value)], className)}>
      {!known && <option value={value}>{l.taskStatus(value)}</option>}
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

/** Engineer select (people who hold at least one Autotask role). The role is the person's default unless chosen. */
export function AssigneeSelect({ value, onChange, disabled, className, allowNone = true }: {
  value: number | null; onChange: (resourceId: number | null) => void; disabled?: boolean; className?: string; allowNone?: boolean;
}) {
  const { data } = useProjectPeople();
  const people = data?.people ?? [];
  const known = value == null || people.some((p) => p.id === value);
  return (
    <select value={value ?? ''} disabled={disabled} onClick={(e) => e.stopPropagation()}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} className={cn(selectCls, className)}>
      {allowNone && <option value="">Unassigned</option>}
      {!known && <option value={value!}>#{value}</option>}
      {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
    </select>
  );
}

export function RoleSelect({ resourceId, value, onChange, disabled }: { resourceId: number; value: number | null; onChange: (roleId: number) => void; disabled?: boolean }) {
  const { data } = useProjectPeople();
  const person = data?.people.find((p) => p.id === resourceId);
  if (!person) return null;
  return (
    <select value={value ?? person.defaultRoleId ?? ''} disabled={disabled} onChange={(e) => onChange(Number(e.target.value))} className={selectCls}>
      {person.roles.map((r) => <option key={r.roleId} value={r.roleId}>{r.roleName}{r.isDefault ? ' (default)' : ''}</option>)}
    </select>
  );
}

/** A task date (YYYY-MM-DD) — the shared DatePicker, red when the task is overdue. */
export function DateInput({ value, onChange, disabled, className, overdue }: { value: string | null; onChange: (v: string | null) => void; disabled?: boolean; className?: string; overdue?: boolean }) {
  return <DatePicker value={value} onChange={onChange} disabled={disabled} className={className} tone={overdue ? 'danger' : 'default'} placeholder="No date" />;
}

/** Hours input that commits on blur / Enter. */
export function HoursInput({ value, onCommit, disabled, className }: { value: number | null; onCommit: (v: number) => void; disabled?: boolean; className?: string }) {
  const [text, setText] = useState(value != null ? String(value) : '');
  useEffect(() => { setText(value != null ? String(value) : ''); }, [value]);
  const commit = () => {
    const n = Number(text);
    if (text.trim() === '' || !Number.isFinite(n) || n < 0 || n === (value ?? -1)) { setText(value != null ? String(value) : ''); return; }
    onCommit(Math.round(n * 100) / 100);
  };
  return (
    <input inputMode="decimal" value={text} disabled={disabled} placeholder="—" onClick={(e) => e.stopPropagation()}
      onChange={(e) => setText(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      className={cn('h-7 w-14 rounded-md border border-input bg-background px-1.5 text-right text-xs tabular-nums text-foreground disabled:opacity-50', className)} />
  );
}

// ---------------- Per-task timers (survive page changes; one per task) ----------------

const TIMER_KEY = 'cosmos:psa:timers';
const TIMER_EVENT = 'cosmos:timers';
type Timers = Record<string, number>;

function readTimers(): Timers {
  try { return JSON.parse(localStorage.getItem(TIMER_KEY) ?? '{}') as Timers; } catch { return {}; }
}
function writeTimers(t: Timers) {
  try { localStorage.setItem(TIMER_KEY, JSON.stringify(t)); } catch { /* storage unavailable */ }
  window.dispatchEvent(new Event(TIMER_EVENT));
}

/** Running timers keyed by task id (start epoch ms), synced across components and tabs. */
export function useTaskTimers() {
  const [timers, setTimers] = useState<Timers>(readTimers);
  useEffect(() => {
    const sync = () => setTimers(readTimers());
    window.addEventListener(TIMER_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => { window.removeEventListener(TIMER_EVENT, sync); window.removeEventListener('storage', sync); };
  }, []);
  const start = useCallback((taskId: number) => writeTimers({ ...readTimers(), [taskId]: Date.now() }), []);
  const stop = useCallback((taskId: number) => { const t = readTimers(); delete t[taskId]; writeTimers(t); }, []);
  return { timers, start, stop };
}

/** Re-renders every second while `active`. */
export function useTick(active: boolean) {
  const [, set] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => set((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [active]);
}

export function fmtElapsed(ms: number) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); const sec = s % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`;
}
